import { TransactionState, useNftx } from '@nftx/react';
import { ActionItem, ActionList } from '../../modules/ActionList';
import useMigrateSlp from '@/application/write/migrate/useMigrateSlp';
import useSignRemoveLiquidity from '@/application/write/migrate/useSignRemoveLiquidity';
import { V2Position, V2Vault } from '@/core/migrate';
import { Vault, getChainConstant, isApproved } from 'nftx.js';
import { ReactNode } from 'react';
import ApproveItem from '../../modules/ActionList/ApproveItem';
import { MIGRATOR_ZAP } from '@/domain/migrate';
import { useIsSafeApp } from '@/application/read/web3/useIsSafeApp';
import { SpinnerGap } from '@/components/elements/Icon/icons/SpinnerGap';

const MigrateSlp = ({
  fullRange,
  migrateError,
  migrateSlp,
  migrateStatus,
  position,
  signRemove,
  signRemoveError,
  signRemoveStatus,
  v2Vault,
  v3Vault,
  onCancel,
  onComplete,
  signData,
  showXSlpSteps,
}: {
  fullRange: boolean;
  position: V2Position;
  v2Vault: V2Vault;
  v3Vault: Vault;
  onCancel: () => void;
  onComplete: () => void;
  signRemoveStatus: TransactionState;
  signRemoveError: any;
  signRemove: ReturnType<typeof useSignRemoveLiquidity>['mutate'];
  migrateStatus: TransactionState;
  migrateError: any;
  migrateSlp: ReturnType<typeof useMigrateSlp>[0];
  signData: any;
  showXSlpSteps: boolean;
}) => {
  const { network } = useNftx();
  const { data: isSafeApp, isLoading } = useIsSafeApp();

  if (isLoading)
    return (
      <div className="p-6 flex justify-center items-center">
        <SpinnerGap className="animate-spin h-6 w-6" />
      </div>
    );

  const actionItems: ReactNode[] = [];

  if (showXSlpSteps) {
    actionItems.push(
      <ActionItem
        label="Unstake Liquidity"
        index={0}
        key={0}
        status="Success"
        error={null}
        action={() => undefined}
      />,
      <ActionItem
        label="Transfer SLP"
        index={1}
        key={1}
        status="Success"
        error={null}
        action={() => undefined}
      />
    );
  }

  /*
   * Permit Flow. EOA's can handle Permit but contracts can't, so for:
   * Gnosis Safe - we do a regular approval
   * Regular EOAs, we carry out an EIP712 PERMIT
   */
  if (isSafeApp) {
    actionItems.push(
      <ApproveItem
        index={actionItems.length}
        key={actionItems.length}
        type="on-chain"
        spenderAddress={getChainConstant(MIGRATOR_ZAP, network)}
        standard="ERC20"
        tokenAddress={v2Vault.lpStakingPool.stakingToken.id}
        symbol="Unstake"
        amount={position.slp}
      />
    );
  } else {
    actionItems.push(
      <ActionItem
        label="Approve Unstake"
        index={actionItems.length}
        key={actionItems.length}
        status={signRemoveStatus}
        error={signRemoveError}
        action={async () => {
          signRemove({
            vault: v2Vault,
            position,
            v3VaultId: v3Vault.vaultId,
          });
        }}
      />
    );
  }

  actionItems.push(
    <ActionItem
      label="Migrate Liquidity"
      index={actionItems.length}
      key={actionItems.length}
      status={migrateStatus}
      error={migrateError}
      action={() => {
        migrateSlp({
          position,
          vault: v2Vault,
          signData,
          v3Vault,
          fullRange,
        });
      }}
    />
  );

  return (
    <div>
      <ActionList
        description=""
        title="Migrate Liquidity"
        onComplete={onComplete}
        onCancel={onCancel}
      >
        {actionItems}
      </ActionList>
    </div>
  );
};

export default MigrateSlp;
